import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Infinity as InfinityIcon, Users, Ticket, Plus, Trash2, Save, ShieldCheck, ScrollText, Search, Download, Inbox, MailCheck, Reply, Image as ImageIcon, Ban, Power, UserX, CreditCard, Cog, BookOpen } from "lucide-react";
import AdminBetalningar from "@/pages/AdminBetalningar";
import AdminGuide from "@/pages/AdminGuide";

function UserRow({ u, onUpdate, onStatus, onDelete }) {
  const [quota, setQuota] = useState(u.cv_quota ?? 6);
  const unlimited = u.role === "admin" || u.plan === "unlimited";
  const status = u.status || "active";
  const statusBadge = status === "suspended"
    ? <Badge className="rounded-full ml-1 bg-amber-100 text-amber-800 border border-amber-200">Avstängd</Badge>
    : status === "blocked"
    ? <Badge className="rounded-full ml-1 bg-rose-100 text-rose-800 border border-rose-200">Blockerad</Badge>
    : null;

  return (
    <Card className="rounded-2xl border-border p-4" data-testid={`admin-user-${u.candidate_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold truncate">{u.name} {u.role === "admin" && <Badge variant="secondary" className="rounded-full ml-1">Admin</Badge>}{statusBadge}</div>
          <div className="text-sm text-muted-foreground">{u.email} · <span className="font-mono text-primary">{u.candidate_id}</span></div>
          <div className="text-sm text-muted-foreground mt-0.5">Använt: {u.cv_used ?? 0} CV:n · {unlimited ? "Obegränsat" : `Kvot ${u.cv_quota ?? 6}`}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid={`toggle-role-${u.candidate_id}`}
            size="sm"
            variant={u.role === "admin" ? "default" : "outline"}
            className="rounded-full min-h-[44px]"
            onClick={() => onUpdate(u.candidate_id, { role: u.role === "admin" ? "candidate" : "admin" })}
          >
            <ShieldCheck className="h-3.5 w-3.5 mr-1" /> {u.role === "admin" ? "Ta bort admin" : "Gör till admin"}
          </Button>
          <Button
            data-testid={`toggle-plan-${u.candidate_id}`}
            size="sm"
            variant={u.plan === "unlimited" ? "default" : "outline"}
            className="rounded-full min-h-[44px]"
            disabled={u.role === "admin"}
            onClick={() => onUpdate(u.candidate_id, { plan: u.plan === "unlimited" ? "free" : "unlimited" })}
          >
            <InfinityIcon className="h-3.5 w-3.5 mr-1" /> {u.plan === "unlimited" ? "Obegränsat på" : "Ge obegränsat"}
          </Button>
          <div className="flex items-center gap-1">
            <Input type="number" className="w-20 h-11" value={quota} onChange={(e) => setQuota(parseInt(e.target.value || "0", 10))} data-testid={`quota-input-${u.candidate_id}`} />
            <Button size="sm" variant="ghost" className="rounded-full min-h-[44px] min-w-[44px]" onClick={() => onUpdate(u.candidate_id, { cv_quota: quota })} data-testid={`set-quota-${u.candidate_id}`}><Save className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
      {u.role !== "admin" && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border">
          {status === "active" ? (
            <Button size="sm" variant="outline" className="rounded-full min-h-[44px] text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => onStatus(u, "suspended")} data-testid={`suspend-${u.candidate_id}`}>
              <Power className="h-3.5 w-3.5 mr-1" /> Stäng av
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="rounded-full min-h-[44px] text-green-700 border-green-300 hover:bg-green-50" onClick={() => onStatus(u, "active")} data-testid={`activate-${u.candidate_id}`}>
              <Power className="h-3.5 w-3.5 mr-1" /> Aktivera
            </Button>
          )}
          {status !== "blocked" ? (
            <Button size="sm" variant="outline" className="rounded-full min-h-[44px] text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => onStatus(u, "blocked")} data-testid={`block-${u.candidate_id}`}>
              <Ban className="h-3.5 w-3.5 mr-1" /> Blockera
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="rounded-full min-h-[44px]" onClick={() => onStatus(u, "active")} data-testid={`unblock-${u.candidate_id}`}>
              <Ban className="h-3.5 w-3.5 mr-1" /> Häv blockering
            </Button>
          )}
          <Button size="sm" variant="ghost" className="rounded-full min-h-[44px] text-rose-700 hover:bg-rose-50" onClick={() => onDelete(u)} data-testid={`delete-user-${u.candidate_id}`}>
            <UserX className="h-3.5 w-3.5 mr-1" /> Radera konto
          </Button>
        </div>
      )}
    </Card>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [grant, setGrant] = useState("unlimited");
  const [credits, setCredits] = useState(3);
  const [promoteEmail, setPromoteEmail] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditType, setAuditType] = useState("all");
  const [feedback, setFeedback] = useState([]);
  const [fbCat, setFbCat] = useState("all");
  const [fbStatus, setFbStatus] = useState("all");
  const [statusModal, setStatusModal] = useState(null); // {type:'suspended'|'blocked'|'delete', user}
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState("");
  const [notify, setNotify] = useState(true);
  const [modalBusy, setModalBusy] = useState(false);
  const [tab, setTab] = useState("manage");

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/codes").then((r) => setCodes(r.data)).catch(() => {});
    api.get("/admin/audit").then((r) => setAuditLog(r.data)).catch(() => {});
    api.get("/admin/feedback").then((r) => setFeedback(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const promote = async () => {
    if (!promoteEmail.trim()) { toast.error("Ange en e-postadress"); return; }
    setPromoting(true);
    try {
      const { data } = await api.post("/admin/promote", { email: promoteEmail.trim(), make_admin: true });
      toast.success(`${data.email} är nu admin`);
      setPromoteEmail(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPromoting(false); }
  };

  const updateUser = async (cid, body) => {
    try {
      await api.put(`/admin/users/${cid}`, body);
      toast.success("Uppdaterat");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = (u, status) => {
    if (status === "active") { doStatus(u, "active", "", "", true); return; }
    setStatusModal({ type: status, user: u });
    setReason(""); setDuration(""); setNotify(true);
  };

  const doStatus = async (u, status, reason, duration, notify) => {
    try {
      await api.put(`/admin/users/${u.candidate_id}/status`, { status, reason, duration, notify });
      toast.success(status === "active" ? "Kontot aktiverat" : "Kontostatus uppdaterad");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteUser = (u) => {
    setStatusModal({ type: "delete", user: u });
    setReason(""); setNotify(true);
  };

  const confirmModal = async () => {
    if (!statusModal) return;
    setModalBusy(true);
    const u = statusModal.user;
    try {
      if (statusModal.type === "delete") {
        await api.delete(`/admin/users/${u.candidate_id}`, { params: { reason, notify } });
        toast.success("Kontot raderat");
      } else {
        await api.put(`/admin/users/${u.candidate_id}/status`, {
          status: statusModal.type, reason,
          duration: statusModal.type === "suspended" ? duration : "", notify,
        });
        toast.success("Kontostatus uppdaterad");
      }
      setStatusModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setModalBusy(false); }
  };

  const createCode = async () => {
    if (!newCode.trim()) { toast.error("Ange en kod"); return; }
    try {
      await api.post("/admin/codes", { code: newCode, grant, credits: grant === "credits" ? credits : 0 });
      toast.success("Kod skapad");
      setNewCode(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteCode = async (code) => { await api.delete(`/admin/codes/${code}`); toast.success("Kod inaktiverad"); load(); };

  const exportAudit = async () => {
    try {
      const res = await api.get("/admin/audit/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hamn-handelselogg-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Händelselogg exporterad");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setFeedbackStatus = async (fid, status) => {
    try {
      await api.put(`/admin/feedback/${fid}`, { status });
      setFeedback((prev) => prev.map((f) => (f.id === fid ? { ...f, status } : f)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewAttachment = async (attId) => {
    try {
      const res = await api.get(`/admin/feedback/attachment/${attId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const replyTo = (f) => {
    const subject = encodeURIComponent(`Re: ${f.category_label} – HAMN CV Studio`);
    const body = encodeURIComponent(`Hej ${f.name || ""}!\n\nTack för ditt meddelande till HAMN CV Studio.\n\n> ${(f.message || "").replace(/\n/g, "\n> ")}\n\n`);
    window.location.href = `mailto:${f.email}?subject=${subject}&body=${body}`;
  };

  const deleteFeedback = async (fid) => {
    if (!window.confirm("Ta bort detta meddelande permanent? Eventuell bifogad skärmdump raderas också.")) return;
    try {
      await api.delete(`/admin/feedback/${fid}`);
      setFeedback((prev) => prev.filter((f) => f.id !== fid));
      toast.success("Meddelandet raderat");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;
  if (users === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  const auditTypes = Array.from(new Map(auditLog.map((e) => [e.action, e.label])).entries());
  const filteredAudit = auditLog.filter((e) => {
    if (auditType !== "all" && e.action !== auditType) return false;
    const q = auditSearch.trim().toLowerCase();
    if (q) return (e.email || "").toLowerCase().includes(q) || (e.label || "").toLowerCase().includes(q);
    return true;
  });

  const filteredFeedback = feedback.filter((f) => {
    if (fbCat !== "all" && f.category !== fbCat) return false;
    if (fbStatus !== "all" && f.status !== fbStatus) return false;
    return true;
  });

  return (
    <div>
      <PageHeader title="Adminpanel" description="Hantera användare, ge gratis/obegränsad åtkomst och skapa accesskoder för demo och kampanjer." />

      <div className="flex flex-wrap gap-2 mb-6">
        <Button variant={tab === "manage" ? "default" : "outline"} className="rounded-full" onClick={() => setTab("manage")} data-testid="admin-tab-manage"><Cog className="h-4 w-4 mr-1.5" /> Hantering</Button>
        <Button variant={tab === "payments" ? "default" : "outline"} className="rounded-full" onClick={() => setTab("payments")} data-testid="admin-tab-payments"><CreditCard className="h-4 w-4 mr-1.5" /> Betalningar</Button>
        <Button variant={tab === "guide" ? "default" : "outline"} className="rounded-full" onClick={() => setTab("guide")} data-testid="admin-tab-guide"><BookOpen className="h-4 w-4 mr-1.5" /> Sidtexter</Button>
      </div>

      {tab === "payments" && <AdminBetalningar />}
      {tab === "guide" && <AdminGuide />}

      {tab === "manage" && (<>
      <Card className="rounded-2xl border-border p-6 mb-6" data-testid="promote-card">
        <div className="flex items-center gap-2 font-head font-semibold text-lg mb-2"><ShieldCheck className="h-5 w-5 text-primary" /> Gör en användare till admin</div>
        <p className="text-sm text-muted-foreground mb-4">Ange e-postadressen till en befintlig användare (de måste ha registrerat ett konto). De får full adminåtkomst och obegränsad kvot – deras lösenord ändras inte.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1 flex-1 min-w-[240px]">
            <Label>E-postadress</Label>
            <Input value={promoteEmail} onChange={(e) => setPromoteEmail(e.target.value)} placeholder="namn@exempel.se" data-testid="promote-email-input" />
          </div>
          <Button className="rounded-full" onClick={promote} disabled={promoting} data-testid="promote-btn">
            {promoting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />} Gör till admin
          </Button>
        </div>
      </Card>

      <Card className="rounded-2xl border-border p-6 mb-6">
        <div className="flex items-center gap-2 font-head font-semibold text-lg mb-4"><Ticket className="h-5 w-5 text-primary" /> Accesskoder</div>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="space-y-1">
            <Label>Kod</Label>
            <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} placeholder="HAMNFRI" data-testid="new-code-input" className="w-40" />
          </div>
          <div className="space-y-1">
            <Label>Typ</Label>
            <Select value={grant} onValueChange={setGrant}>
              <SelectTrigger className="w-44" data-testid="grant-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unlimited">Obegränsat</SelectItem>
                <SelectItem value="credits">Extra CV-krediter</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {grant === "credits" && (
            <div className="space-y-1">
              <Label>Antal CV:n</Label>
              <Input type="number" value={credits} onChange={(e) => setCredits(parseInt(e.target.value || "0", 10))} className="w-24" />
            </div>
          )}
          <Button className="rounded-full" onClick={createCode} data-testid="create-code-btn"><Plus className="h-4 w-4 mr-1" /> Skapa kod</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {codes.filter((c) => c.active).length === 0 && <p className="text-sm text-muted-foreground">Inga aktiva koder.</p>}
          {codes.filter((c) => c.active).map((c) => (
            <Badge key={c.code} variant="secondary" className="rounded-full pl-3 pr-1 py-1.5 text-sm">
              <span className="font-mono">{c.code}</span>
              <span className="mx-1.5 text-muted-foreground">· {c.grant === "unlimited" ? "obegränsat" : `${c.credits} CV`} · använd {c.used_count}x</span>
              <button className="flex h-11 w-11 items-center justify-center hover:text-destructive" onClick={() => deleteCode(c.code)} data-testid={`delete-code-${c.code}`}><Trash2 className="h-4 w-4" /></button>
            </Badge>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-2 font-head font-semibold text-lg mb-3"><Users className="h-5 w-5 text-primary" /> Användare ({users.length})</div>
      <div className="grid gap-3">
        {users.map((u) => <UserRow key={u.candidate_id} u={u} onUpdate={updateUser} onStatus={setStatus} onDelete={deleteUser} />)}
      </div>

      <Card className="rounded-2xl border-border p-6 mt-6" data-testid="feedback-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 font-head font-semibold text-lg">
            <Inbox className="h-5 w-5 text-primary" /> Hjälp & feedback
            {feedback.filter((f) => f.status === "ny").length > 0 && (
              <Badge className="rounded-full">{feedback.filter((f) => f.status === "ny").length} nya</Badge>
            )}
          </div>
          {feedback.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Select value={fbCat} onValueChange={setFbCat}>
                <SelectTrigger className="h-9 w-full sm:w-44" data-testid="feedback-cat-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla kategorier</SelectItem>
                  <SelectItem value="fraga">Fråga / Hjälp</SelectItem>
                  <SelectItem value="feedback">Feedback</SelectItem>
                  <SelectItem value="bugg">Buggrapport</SelectItem>
                </SelectContent>
              </Select>
              <Select value={fbStatus} onValueChange={setFbStatus}>
                <SelectTrigger className="h-9 w-full sm:w-40" data-testid="feedback-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla statusar</SelectItem>
                  <SelectItem value="ny">Nya</SelectItem>
                  <SelectItem value="besvarad">Besvarade</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {feedback.length === 0 ? (
          <p className="text-sm text-muted-foreground">Inga meddelanden ännu.</p>
        ) : filteredFeedback.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="feedback-no-match">Inga meddelanden matchar filtret.</p>
        ) : (
          <ul className="space-y-3 max-h-[460px] overflow-y-auto pr-1" data-testid="feedback-list">
            {filteredFeedback.map((f) => (
              <li key={f.id} className={`rounded-xl border p-4 ${f.status === "ny" ? "border-primary/30 bg-primary/5" : "border-border"}`} data-testid={`feedback-item-${f.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="secondary" className="rounded-full">{f.category_label}</Badge>
                    <span className="text-sm font-medium truncate">{f.name || "Anonym"}</span>
                    {f.email && <span className="text-xs text-muted-foreground truncate">· {f.email}</span>}
                    {f.source === "public" && <Badge variant="outline" className="rounded-full text-[10px]">Ej inloggad</Badge>}
                  </div>
                  <span className="text-xs text-muted-foreground">{f.created_at ? new Date(f.created_at).toLocaleString("sv-SE") : ""}</span>
                </div>
                <p className="text-sm text-foreground/90 whitespace-pre-wrap">{f.message}</p>
                {f.attachment_id && (
                  <button onClick={() => viewAttachment(f.attachment_id)} className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline min-h-[44px]" data-testid={`feedback-view-attachment-${f.id}`}>
                    <ImageIcon className="h-3.5 w-3.5" /> Visa skärmdump{f.attachment_filename ? ` (${f.attachment_filename})` : ""}
                  </button>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {f.email && (
                    <Button size="sm" variant="outline" className="rounded-full min-h-[44px]" onClick={() => replyTo(f)} data-testid={`feedback-reply-${f.id}`}>
                      <Reply className="h-3.5 w-3.5 mr-1" /> Svara
                    </Button>
                  )}
                  {f.status === "ny" ? (
                    <Button size="sm" variant="outline" className="rounded-full min-h-[44px]" onClick={() => setFeedbackStatus(f.id, "besvarad")} data-testid={`feedback-mark-answered-${f.id}`}>
                      <MailCheck className="h-3.5 w-3.5 mr-1" /> Markera som besvarad
                    </Button>
                  ) : (
                    <button className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 min-h-[44px]" onClick={() => setFeedbackStatus(f.id, "ny")} data-testid={`feedback-reopen-${f.id}`}>
                      <MailCheck className="h-3.5 w-3.5 text-primary" /> Besvarad · återöppna
                    </button>
                  )}
                  <Button size="sm" variant="ghost" className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto min-h-[44px]" onClick={() => deleteFeedback(f.id)} data-testid={`feedback-delete-${f.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Ta bort
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="rounded-2xl border-border p-6 mt-6" data-testid="audit-log-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 font-head font-semibold text-lg"><ScrollText className="h-5 w-5 text-primary" /> Händelselogg</div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="audit-search"
                placeholder="Sök e-post eller händelse…"
                className="h-9 pl-8 w-full sm:w-56"
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
              />
            </div>
            <Select value={auditType} onValueChange={setAuditType}>
              <SelectTrigger className="h-9 w-full sm:w-52" data-testid="audit-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla händelsetyper</SelectItem>
                {auditTypes.map(([action, label]) => (
                  <SelectItem key={action} value={action}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9 rounded-full" onClick={exportAudit} data-testid="audit-export-btn"><Download className="h-4 w-4 mr-1.5" /> Exportera CSV</Button>
          </div>
        </div>
        {auditLog.length === 0 ? (
          <p className="text-sm text-muted-foreground">Inga händelser ännu.</p>
        ) : filteredAudit.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="audit-no-match">Inga händelser matchar filtret.</p>
        ) : (
          <ul className="divide-y divide-border max-h-[420px] overflow-y-auto pr-1" data-testid="audit-log-list">
            {filteredAudit.map((e, i) => (
              <li key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${e.action === "login_locked" ? "bg-destructive" : e.action.startsWith("password") ? "bg-[hsl(var(--brand-azure))]" : e.action.startsWith("admin") ? "bg-primary" : "bg-muted-foreground/40"}`} />
                  <span className="font-medium">{e.label}</span>
                  {e.email && <span className="text-muted-foreground truncate">· {e.email}</span>}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{e.created_at ? new Date(e.created_at).toLocaleString("sv-SE") : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      </>)}

      <Dialog open={!!statusModal} onOpenChange={(o) => !o && setStatusModal(null)}>
        <DialogContent className="max-w-md" data-testid="account-action-dialog">
          {statusModal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-head">
                  {statusModal.type === "delete" ? "Radera konto" : statusModal.type === "blocked" ? "Blockera konto" : "Stäng av konto"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {statusModal.type === "delete"
                    ? <>Kontot för <strong>{statusModal.user?.email}</strong> och all data raderas permanent. Detta kan inte ångras.</>
                    : statusModal.type === "blocked"
                    ? <>Kontot för <strong>{statusModal.user?.email}</strong> stängs av och e-postadressen kan inte registrera nytt konto.</>
                    : <>Kontot för <strong>{statusModal.user?.email}</strong> stängs av tillfälligt. All data behålls.</>}
                </p>
                <div>
                  <Label className="text-sm">Anledning {statusModal.type !== "delete" && "(visas för användaren)"}</Label>
                  <Textarea className="mt-1.5" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder={statusModal.type === "delete" ? "Intern/valfri anledning" : "T.ex. Brott mot användarvillkoren"} data-testid="account-action-reason" />
                </div>
                {statusModal.type === "suspended" && (
                  <div>
                    <Label className="text-sm">Varaktighet (valfritt)</Label>
                    <Input className="mt-1.5 h-11" value={duration} onChange={(e) => setDuration(e.target.value)}
                      placeholder="T.ex. 2 veckor, tills vidare, till 2026-09-01" data-testid="account-action-duration" />
                  </div>
                )}
                <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid="account-action-notify">
                  <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} />
                  Skicka ett vänligt e-postmeddelande till användaren
                </label>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => setStatusModal(null)} data-testid="account-action-cancel">Avbryt</Button>
                <Button
                  className={`rounded-full ${statusModal.type !== "suspended" ? "bg-rose-600 hover:bg-rose-700" : ""}`}
                  onClick={confirmModal} disabled={modalBusy} data-testid="account-action-confirm">
                  {modalBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {statusModal.type === "delete" ? "Radera permanent" : statusModal.type === "blocked" ? "Blockera" : "Stäng av"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
